import { DeleteIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import React from "react";
import { OfficeBranch } from "../office-branch.types";
import { OfficeId } from "../../../shared/schema/schema";

interface Props {
  branches: OfficeBranch[];
  offices: Array<{ id: OfficeId; name: string }>;
  onAdd: (officeId?: OfficeId) => void;
  onDelete: (branch: OfficeBranch) => void;
  onEdit: (branch: OfficeBranch) => void;
}

const OfficeBranchTable = (props: Props) => {
  const sortedOffices = React.useMemo(
    () => [...props.offices].sort((a, b) => a.name.localeCompare(b.name)),
    [props.offices]
  );

  const branchesByOffice = React.useMemo(() => {
    const map = new Map<number, OfficeBranch[]>();
    for (const office of sortedOffices) {
      map.set(office.id, []);
    }
    for (const branch of props.branches) {
      const existing = map.get(branch.officeId);
      if (existing !== undefined) {
        existing.push(branch);
      } else {
        map.set(branch.officeId, [branch]);
      }
    }
    return map;
  }, [props.branches, sortedOffices]);

  return (
    <Box>
      {sortedOffices.map((office) => {
        const branches = branchesByOffice.get(office.id) ?? [];

        return (
          <Box key={office.id} mb={8}>
            <Flex align="center" justify="space-between" mb={3}>
              <Heading size="sm">{office.name}</Heading>
              <Button
                colorScheme="blue"
                size="sm"
                variant="outline"
                onClick={() => props.onAdd(office.id)}
              >
                + New
              </Button>
            </Flex>

            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>License #</Th>
                  <Th>Counties</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {branches.length === 0 ? (
                  <Tr>
                    <Td colSpan={4}>
                      <Text color="gray.500">No branches</Text>
                    </Td>
                  </Tr>
                ) : (
                  branches.map((branch) => (
                    <Tr
                      key={branch.id}
                      _hover={{ bg: "gray.50" }}
                      cursor="pointer"
                      onClick={() => props.onEdit(branch)}
                    >
                      <Td>{branch.name}</Td>
                      <Td>{branch.licenseNumber ?? <Text color="gray.400">—</Text>}</Td>
                      <Td>
                        <Wrap spacing={1}>
                          {branch.counties.map((county) => (
                            <WrapItem key={`${county.state}::${county.countyName}`}>
                              <Tag colorScheme="gray" size="sm">
                                {county.countyName}, {county.state}
                              </Tag>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </Td>
                      <Td>
                        <IconButton
                          aria-label="Delete branch"
                          colorScheme="red"
                          icon={<DeleteIcon />}
                          size="xs"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            props.onDelete(branch);
                          }}
                        />
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        );
      })}
    </Box>
  );
};

export default OfficeBranchTable;
